import type { AccountingEntry } from '@models';

/**
 * Criterio compartido para las vistas que suman A LA VEZ movimientos
 * (`Movement`) y apuntes contables (`AccountingEntry`): saldo del club
 * (`ClubsService.clubBalance$`, `ClubManagerService.clubBalance$`), informes
 * económico y principal, y la tabla de movimientos de la ficha del club.
 *
 * Un apunte con `movementId` es el reflejo contable de un cobro con tarjeta
 * («Registrar como apunte contable» en Contabilidad): el importe ya está en el
 * movimiento de origen, así que sumarlo también lo contaría dos veces.
 *
 * Devuelve los apuntes que SÍ deben sumarse junto a `movements`:
 * - sin `movementId` → se cuentan (apunte manual, p. ej. transferencia);
 * - con `movementId` de un movimiento presente en `movements` → se descartan
 *   (el movimiento ya se cuenta);
 * - con `movementId` de un movimiento que NO está en `movements` (borrado, o
 *   fuera del conjunto que la vista suma) → se cuentan, para no perder el
 *   ingreso: el apunte es entonces la única constancia del importe en la vista.
 *
 * Solo se usa donde se combinan movimientos + apuntes. La contabilidad oficial
 * (pantalla Contabilidad, presupuesto, IVA) trabaja solo con apuntes y NO debe
 * filtrarlos.
 */
export function excludeEntriesLinkedToMovements<
  E extends Pick<AccountingEntry, 'movementId'>,
>(entries: readonly E[], movements: readonly { id?: string }[]): E[] {
  const movementIds = new Set<string>();
  for (const m of movements) if (m.id) movementIds.add(m.id);
  return entries.filter((e) => !e.movementId || !movementIds.has(e.movementId));
}
